import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUtils';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import SimpleLoader from '../components/SimpleLoader';
import NotificationSettings from '../components/NotificationSettings';
import {
  showChatNotification,
  showImageNotification,
  isNotificationEnabled,
  getNotificationPermission
} from '../utils/notificationUtils';

// Handle ResizeObserver errors globally
window.addEventListener('error', e => {
  if (e.message === 'ResizeObserver loop limit exceeded' ||
    e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
});

const Chat = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingImage, setSendingImage] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [newChatData, setNewChatData] = useState(null);
  const [isNewChat, setIsNewChat] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const loadChats = async (showNotifications = false) => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await api.get('/chat/my-chats');
      const validChats = response.data.chats.filter(
        chat => chat.buyer?._id !== chat.seller?._id
      );

      if (showNotifications && chats.length > 0 && isNotificationEnabled()) {
        checkForNewMessages(chats, validChats);
      }

      setChats(validChats);

      if (validChats.length > 0 && !selectedChat) {
        setSelectedChat(validChats[0]);
        setChatMessages(validChats[0].messages || []);
      }
    } catch {
      toast.error('Error loading chats');
    } finally {
      setLoading(false);
    }
  };

  const checkForNewMessages = (oldChats, newChats) => {
    if (!user || !isNotificationEnabled()) return;

    const currentUserId = user.id || user._id;

    newChats.forEach(newChat => {
      const oldChat = oldChats.find(c => c._id === newChat._id);
      if (!oldChat) return;

      const oldMessageCount = oldChat.messages?.length || 0;
      const newMessageCount = newChat.messages?.length || 0;

      if (newMessageCount > oldMessageCount) {
        const newMessages = newChat.messages.slice(oldMessageCount);

        newMessages.forEach(message => {
          const senderId = message.sender?._id || message.sender;

          if (senderId !== currentUserId) {
            const otherParticipant = getOtherParticipant(newChat);
            const senderName = otherParticipant?.name || 'Someone';
            const listingName = newChat.listing?.itemName || 'Unknown Item';

            if (message.messageType === 'image') {
              showImageNotification(
                senderName,
                message.imageCaption || '',
                newChat._id,
                listingName
              );
            } else {
              showChatNotification(
                senderName,
                message.message,
                newChat._id,
                listingName
              );
            }
          }
        });
      }
    });
  };
  useEffect(() => {
    if (getNotificationPermission() === 'default') {
      setTimeout(() => {
        setShowNotificationSettings(true);
      }, 2000);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadChats();

      if (location.state?.newChatData) {
        setNewChatData(location.state.newChatData);
        setIsNewChat(true);
        setSelectedChat(null);
        setChatMessages([]);
        window.history.replaceState({}, document.title);
      }
    }
  }, [user, location.state]);

  const scrollToBottom = useCallback(() => {
    // Only scroll if we have a messages container and messages
    if (messagesEndRef.current && chatMessages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages.length]);

  useEffect(() => {
    // Only scroll to bottom if there are messages and a chat is selected
    if (chatMessages.length > 0 && (selectedChat || isNewChat)) {
      scrollToBottom();
    }
  }, [chatMessages, scrollToBottom, selectedChat, isNewChat]);

  const selectChat = useCallback((chat) => {
    setSelectedChat(chat);
    setChatMessages(chat.messages || []);
    markMessagesAsRead(chat._id);
    
    // Only scroll to bottom if there are messages
    if (chat.messages && chat.messages.length > 0) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  }, []);

  const markMessagesAsRead = async (chatId) => {
    try {
      await api.post(`/chat/${chatId}/mark-read`);
    } catch (error) {
    }
  };
  const sendMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;

    const msg = newMessage.trim();
    setNewMessage('');
    setSendingMessage(true);

    try {
      let response;

      if (isNewChat && newChatData) {
        response = await api.post('/chat/send-first-message', {
          listingId: newChatData.listing._id,
          message: msg
        });

        setIsNewChat(false);
        setNewChatData(null);
        setSelectedChat(response.data.chat);
        setChatMessages(response.data.chat.messages);
        loadChats();

      } else if (selectedChat) {
        response = await api.post(
          `/chat/${selectedChat._id}/message`,
          { message: msg }
        );

        setChatMessages(response.data.chat.messages);
        setChats(prev =>
          prev.map(chat =>
            chat._id === selectedChat._id
              ? { ...chat, messages: response.data.chat.messages }
              : chat
          )
        );
      } else {
        toast.error('No chat selected');
        return;
      }

      // Scroll to bottom after sending message
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error sending message';

      if (errorMessage.includes('cannot chat with yourself')) {
        toast.error('You cannot chat about your own listing');
      } else if (error.response?.status === 401) {
        toast.error('Please login to send messages');
      } else if (error.response?.status === 403) {
        toast.error('Access denied');
      } else if (error.response?.status === 404) {
        toast.error('Chat not found');
      } else {
        toast.error(errorMessage);
      }

      setNewMessage(msg);
    } finally {
      setSendingMessage(false);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };
  const sendImage = async (file, caption = '') => {
    if (!file || !selectedChat || sendingImage) return;

    setSendingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      const response = await api.post(
        `/chat/${selectedChat._id}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setChatMessages(response.data.chat.messages);
      setChats(prev =>
        prev.map(chat =>
          chat._id === selectedChat._id
            ? { ...chat, messages: response.data.chat.messages }
            : chat
        )
      );

      // Scroll to bottom after sending image
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);

      toast.success('Image sent successfully!');

    } catch (error) {
      toast.error('Error sending image');
    } finally {
      setSendingImage(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    setImageCaption('');
    setShowImagePreview(true);
    event.target.value = '';
  };

  const handleSendImage = () => {
    if (selectedImage) {
      sendImage(selectedImage, imageCaption);
      handleCancelImage();
    }
  };

  const handleCancelImage = () => {
    setShowImagePreview(false);
    setSelectedImage(null);
    setImageCaption('');
  };
  const getOtherParticipant = (chat) => {
    if (!chat || !user) return null;

    const currentUserId = user.id || user._id;

    if (currentUserId === chat.seller?._id) {
      return chat.buyer;
    }

    if (currentUserId === chat.buyer?._id) {
      return chat.seller;
    }

    return chat.buyer || chat.seller;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp || Date.now()).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f4f8' }}>
        <SimpleLoader size="lg" text="Loading chats..." />
      </div>
    );
  }
  return (
    <>
      <Helmet>
        <title>Messages - VestrioZ</title>
        <meta name="description" content="Chat with other users about rental inquiries and transactions." />
        <meta name="keywords" content="messages, chat, VestrioZ, communication" />
      </Helmet>
      
      <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white shadow-sm" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--sage-green)' }}>
                  <i className="fas fa-comments text-white text-lg"></i>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-black">Messages</h1>
                  <p className="text-gray-700 text-sm">{chats.length} conversations</p>
                </div>
              </div>
              <button
                onClick={() => loadChats()}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 text-black"
                title="Refresh chats"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="rounded-2xl shadow-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 200px)', minHeight: '500px', border: '1px solid #e2e8f0' }}>
            <div className="flex h-full">
              
              {/* Chat List Sidebar */}
              <div className={`${selectedChat || isNewChat ? 'hidden lg:flex' : 'flex'} w-full lg:w-80 flex-col bg-gray-50`} style={{ borderRight: '1px solid #e2e8f0' }}>
                <div className="p-4 bg-white" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Conversations</h2>
                    <span className="text-sm text-white px-3 py-1 rounded-full font-medium" style={{ backgroundColor: 'var(--sage-green)' }}>
                      {chats.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {chats.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-blue-50" style={{ border: '2px solid #dbeafe' }}>
                        <i className="fas fa-inbox text-2xl" style={{ color: 'var(--sage-green)' }}></i>
                      </div>
                      <h3 className="text-lg font-medium text-gray-800 mb-2">No conversations yet</h3>
                      <p className="text-gray-600 text-sm">Start browsing items to begin chatting with sellers</p>
                    </div>
                  ) : (
                    chats.map(chat => {
                      const other = getOtherParticipant(chat);
                      const isSelected = selectedChat?._id === chat._id;
                      const lastMessage = chat.messages?.[chat.messages.length - 1];

                      return (
                        <div
                          key={chat._id}
                          onClick={() => selectChat(chat)}
                          className={`p-4 cursor-pointer transition-all duration-300 rounded-lg mx-2 my-1 ${
                            isSelected ? 'bg-white shadow-lg ring-2 ring-blue-100 border-l-4' : 'hover:bg-white hover:shadow-md'
                          }`}
                          style={isSelected ? { 
                            borderLeftColor: 'var(--sage-green)'
                          } : {}}
                          style={isSelected ? { 
                            backgroundColor: '#f0f9ff', 
                            borderLeftColor: 'var(--sage-green)' 
                          } : {}}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all duration-300" 
                                 style={{ backgroundColor: 'var(--sage-green)' }}>
                              {other?.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h3 className="font-medium text-gray-800 truncate">
                                  {other?.name || 'Unknown User'}
                                </h3>
                                {lastMessage && (
                                  <span className="text-xs text-gray-500">
                                    {formatTime(lastMessage.timestamp || lastMessage.createdAt)}
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-gray-600 truncate mb-1">
                                {chat.listing?.itemName || 'Unknown Item'}
                              </p>
                              
                              {lastMessage && (
                                <p className="text-xs text-gray-500 truncate">
                                  {lastMessage.messageType === 'image' 
                                    ? '📷 Image' 
                                    : lastMessage.message
                                  }
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              {/* Chat Area */}
              <div className={`${selectedChat || isNewChat ? 'flex' : 'hidden lg:flex'} flex-1 flex-col`}>
                {selectedChat || isNewChat ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 bg-white shadow-sm" style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => {
                            if (isNewChat) {
                              setIsNewChat(false);
                              setNewChatData(null);
                            } else {
                              setSelectedChat(null);
                            }
                          }}
                          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <i className="fas fa-arrow-left text-gray-600"></i>
                        </button>
                        
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold shadow-md" 
                             style={{ backgroundColor: 'var(--sage-green)' }}>
                          {isNewChat
                            ? newChatData?.seller?.name?.charAt(0)?.toUpperCase()
                            : getOtherParticipant(selectedChat)?.name?.charAt(0)?.toUpperCase()
                          }
                        </div>
                        
                        <div className="flex-1">
                          <h2 className="font-semibold text-gray-800">
                            {isNewChat
                              ? newChatData?.seller?.name
                              : getOtherParticipant(selectedChat)?.name
                            }
                          </h2>
                          <p className="text-sm text-gray-600">
                            {isNewChat
                              ? newChatData?.listing?.itemName
                              : selectedChat?.listing?.itemName
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                      {isNewChat ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center max-w-md">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg" 
                                 style={{ backgroundColor: 'var(--sage-green)' }}>
                              <i className="fas fa-comments text-white text-2xl"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                              Start a conversation
                            </h3>
                            <p className="text-gray-600 mb-6">
                              You're interested in "{newChatData?.listing?.itemName}". 
                              Send your first message to {newChatData?.seller?.name}!
                            </p>
                            
                            {/* Listing Preview */}
                            <div className="rounded-xl p-4 shadow-sm bg-white" style={{ border: '1px solid #e2e8f0' }}>
                              <div className="flex items-center space-x-3">
                                <img
                                  src={getImageUrl(newChatData?.listing?.images?.[0])}
                                  alt={newChatData?.listing?.itemName}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <div className="text-left">
                                  <h4 className="font-medium text-gray-800">{newChatData?.listing?.itemName}</h4>
                                  <p className="text-sm text-gray-600">₹{newChatData?.listing?.price}/day</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : chatMessages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f0f9ff' }}>
                              <i className="fas fa-comments text-2xl" style={{ color: 'var(--sage-green)' }}></i>
                            </div>
                            <h3 className="text-lg font-medium text-gray-800 mb-2">No messages yet</h3>
                            <p className="text-gray-600">Start the conversation!</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, i) => {
                            const currentUserId = user?.id || user?._id;
                            const isMine = msg.sender?._id === currentUserId || msg.sender === currentUserId;

                            return (
                              <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${isMine ? 'ml-12' : 'mr-12'}`}>
                                  
                                  {/* Message Bubble */}
                                  <div className={`rounded-2xl px-4 py-3 shadow-md hover:shadow-lg transition-all duration-300 ${
                                    isMine 
                                      ? 'text-white rounded-br-md' 
                                      : 'text-gray-800 rounded-bl-md bg-white border-l-4'
                                  }`} style={isMine ? { 
                                    backgroundColor: 'var(--sage-green)'
                                  } : { 
                                    backgroundColor: 'white',
                                    borderLeftColor: 'var(--sage-green)',
                                    border: '1px solid #e2e8f0'
                                  }}>
                                    
                                    {msg.messageType === 'image' ? (
                                      <div>
                                        <img
                                          src={getImageUrl(msg.imageUrl, 'chat')}
                                          alt="Shared image"
                                          className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                                          style={{ maxHeight: '200px', maxWidth: '200px' }}
                                          onClick={() => window.open(getImageUrl(msg.imageUrl, 'chat'), '_blank')}
                                          onError={(e) => {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                          }}
                                        />
                                        {msg.imageCaption && (
                                          <p className="text-sm mt-2 whitespace-pre-wrap">
                                            {msg.imageCaption}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="whitespace-pre-wrap break-words">
                                        {msg.message}
                                      </p>
                                    )}
                                  </div>

                                  {/* Timestamp */}
                                  <p className={`text-xs text-gray-500 mt-1 ${isMine ? 'text-right' : 'text-left'}`}>
                                    {formatTime(msg.timestamp || msg.createdAt)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>
                    {/* Message Input */}
                    <div className="p-4 bg-white" style={{ borderTop: '1px solid #e2e8f0' }}>
                      <div className="flex items-end space-x-3">
                        {/* Hidden file input */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />

                        {/* Image upload button */}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sendingImage}
                          className="p-3 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                          title="Send image"
                        >
                          {sendingImage ? (
                            <i className="fas fa-circle-notch fa-spin"></i>
                          ) : (
                            <i className="fas fa-image"></i>
                          )}
                        </button>

                        {/* Message input */}
                        <div className="flex-1">
                          <textarea
                            ref={textareaRef}
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value.slice(0, 500))}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                            placeholder={isNewChat ? "Write your first message..." : "Type your message..."}
                            className="w-full rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-base min-h-[60px] max-h-[140px] md:min-h-[48px] md:max-h-[120px] bg-white"
                            style={{ border: '1px solid #e2e8f0' }}
                            rows="2"
                          />
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500 hidden sm:block">
                              Press Enter to send, Shift+Enter for new line
                            </span>
                            <span className="text-xs text-gray-500 sm:hidden">
                              Enter to send
                            </span>
                            <span className="text-xs text-gray-500">
                              {newMessage.length}/500
                            </span>
                          </div>
                        </div>

                        {/* Send button */}
                        <button
                          type="button"
                          onClick={sendMessage}
                          disabled={!newMessage.trim() || sendingMessage}
                          className="p-3 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                          style={{ backgroundColor: 'var(--sage-green)' }}
                        >
                          {sendingMessage ? (
                            <i className="fas fa-circle-notch fa-spin"></i>
                          ) : (
                            <i className="fas fa-paper-plane"></i>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-50" style={{ border: '2px solid #dbeafe' }}>
                        <i className="fas fa-comments text-3xl" style={{ color: 'var(--sage-green)' }}></i>
                      </div>
                      <h3 className="text-xl font-medium text-gray-800 mb-2">Select a conversation</h3>
                      <p className="text-gray-600">Choose a chat from the sidebar to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Image Preview Modal */}
        {showImagePreview && selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="rounded-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl bg-white" style={{ border: '1px solid #e2e8f0' }}>
              {/* Modal Header */}
              <div className="p-4 flex items-center justify-between bg-white" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <h3 className="text-lg font-semibold text-gray-800">Send Image</h3>
                <button
                  onClick={handleCancelImage}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-50"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              {/* Image Preview */}
              <div className="p-4">
                <div className="mb-4 bg-gray-50 rounded-lg overflow-hidden">
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="Preview"
                    className="w-full h-auto max-h-64 object-contain"
                  />
                </div>

                {/* Caption Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add a caption (optional)
                  </label>
                  <textarea
                    value={imageCaption}
                    onChange={(e) => setImageCaption(e.target.value.slice(0, 200))}
                    placeholder="Write a caption for your image..."
                    className="w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none bg-white"
                    style={{ border: '1px solid #e2e8f0' }}
                    rows="3"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {imageCaption.length}/200
                  </div>
                </div>

                {/* File Info */}
                <div className="mb-4 text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="fas fa-file-image" style={{ color: 'var(--sage-green)' }}></i>
                    <span>{selectedImage.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-weight-hanging" style={{ color: 'var(--sage-green)' }}></i>
                    <span>{(selectedImage.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancelImage}
                    className="flex-1 px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors bg-white"
                    style={{ border: '1px solid #e2e8f0' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendImage}
                    disabled={sendingImage}
                    className="flex-1 px-4 py-2 text-white rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md"
                    style={{ backgroundColor: 'var(--sage-green)' }}
                  >
                    {sendingImage ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin"></i>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        <span>Send Image</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification Settings Modal */}
        <NotificationSettings
          isOpen={showNotificationSettings}
          onClose={() => setShowNotificationSettings(false)}
        />

        <Footer />
      </div>
    </>
  );
};

export default Chat;